import React, { useState, useMemo, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  Autocomplete,
  Typography,
  Box,
  Chip,
  IconButton,
  Stack,
  Alert,
} from '@mui/material';
import { authContext, Position } from '@staffedupusa/shared-components';
import { Close } from '@mui/icons-material';
import MessageIcon from '@mui/icons-material/Message';
import { useGetHiredApplicants } from '../hooks/useGetHiredApplicants';
import { useCreateTeamMessage } from '../hooks/useCreateTeamMessage';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';
import PurchaseTextsDialog from './PurchaseTextsDialog';
import { useGetHiredApplicantsCount } from '../hooks/useGetHiredApplicantsCount';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

interface TeamMember {
  id: number;
  name: string;
  email: string;
}

interface MessageTeamDialogProps {
  open: boolean;
  onClose: () => void;
  positions: Position[] | [];
}

const SMS_CHAR_LIMIT = 160;
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_KEY);
const MessageTeamDialog: React.FC<MessageTeamDialogProps> = ({
  open,
  onClose,
  positions,
}) => {
  const { authData } = useContext(authContext);
  const MAX_CREDITS = authData?.user?.company?.additional_info?.allocated_texts;
  const usedText =
    authData?.user?.company?.additional_info?.consumed_texts || 0;

  const [errorText, setErrorText] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<TeamMember[]>([]);
  const [selectedPositions, setSelectedPositions] = useState<Position[]>([]);
  const [message, setMessage] = useState('');
  const [sendMethod, setSendMethod] = useState<'email' | 'text' | 'both'>(
    'email',
  );
  const [recipientType, setRecipientType] = useState<'members' | 'positions'>(
    'members',
  );
  const [showPurchaseTextsModal, setShowPurchaseTextsModal] = useState(false);

  const { applicantsData, isLoadingApplicantsData } = useGetHiredApplicants({
    setErrorText,
    enabled: true,
  });

  const { applicantsCount, isLoadingApplicantsCount } =
    useGetHiredApplicantsCount({
      setErrorText,
      position_ids: selectedPositions.map((p) => p.id).join(','),
    });

  const teamMembers = (applicantsData?.objects as TeamMember[]) || [];

  const { createTeamMessage, isCreatingTeamMessage } = useCreateTeamMessage({
    setErrorText,
  });

  const handleSubmit = () => {
    const options = {
      onSuccess: (data: { status: number; message: string }) => {
        if (data && data?.status === 200) {
          toast.success('Message sent successfully');
          setErrorText('');
          onClose();
          // reset();
        } else {
          setErrorText(data.message);
        }
      },
    };

    const payload = {
      message_status: false,
      user_ids:
        recipientType === 'members' ? selectedMembers.map((m) => m.id) : [],
      positions:
        recipientType === 'positions' ? selectedPositions.map((p) => p.id) : [],
      message,
      required_method:
        sendMethod === 'email' ? 0 : sendMethod === 'text' ? 1 : 2,
      applicants_type: recipientType === 'members' ? 0 : 1,
      total_text: messagesPerRecipient,
    };
    createTeamMessage(payload, options);
  };

  const messageLength = message.length;
  const messagesPerRecipient = Math.ceil(messageLength / SMS_CHAR_LIMIT) || 1;

  const recipientsCount =
    sendMethod !== 'email'
      ? recipientType === 'members'
        ? selectedMembers.length === 0
          ? messagesPerRecipient - 1 + usedText
          : messagesPerRecipient * selectedMembers.length + usedText
        : messagesPerRecipient * (applicantsCount?.count || 0) + usedText
      : 0;

  const totalCreditsUsed = recipientsCount;

  const textCount = {
    characters: messageLength,
    messagesPerRecipient,
    totalCreditsUsed,
    remainingCredits: Math.max(MAX_CREDITS - totalCreditsUsed, 0),
    exceedsCredits: totalCreditsUsed > MAX_CREDITS,
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={onClose}
        >
          <Close />
        </IconButton>
        <br />
        <MessageIcon
          sx={{
            fontSize: 60,
            color: 'primary.main',
            margin: 'auto !important',
          }}
        />
        <Typography variant="h4" mt={1}>
          Message Team
        </Typography>
      </DialogTitle>
      <DialogContent>
        {errorText && <Alert severity="error">{errorText}</Alert>}
        <Box mt={2}>
          <Typography variant="subtitle1">Select team members by:</Typography>
          <RadioGroup
            row
            value={recipientType}
            onChange={(e) => {
              setRecipientType(e.target.value as 'members' | 'positions');
              setSelectedMembers([]);
              setSelectedPositions([]);
            }}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
              alignContent: 'center',
            }}
          >
            <FormControlLabel
              value="members"
              control={<Radio />}
              label="Team Members"
            />
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={1}
              sx={{ width: '73%' }}
            >
              <FormControlLabel
                value="positions"
                control={<Radio />}
                label="Positions"
              />
              {recipientType === 'positions' && (
                <>
                  {isLoadingApplicantsCount && (
                    <Typography variant="body2" color="textSecondary" mt={1}>
                      Loading team members count...
                    </Typography>
                  )}
                  {!isLoadingApplicantsCount && applicantsCount && (
                    <Typography variant="body2" color="textSecondary" mt={1}>
                      Total team members: {applicantsCount.count}
                    </Typography>
                  )}
                </>
              )}
            </Stack>
          </RadioGroup>
        </Box>
        {recipientType === 'members' && (
          <Autocomplete
            multiple
            size="small"
            options={[{ id: -1, name: 'Select All' }, ...teamMembers]}
            getOptionLabel={(option) => option.name}
            value={selectedMembers}
            onChange={(_, newValue) => {
              const isSelectAll = newValue.some((member) => member.id === -1);
              if (isSelectAll) {
                const allMembers = teamMembers.filter(
                  (member) => member.id !== -1,
                );
                setSelectedMembers(allMembers);
              } else {
                const filteredMembers = newValue.filter(
                  (member): member is TeamMember => member.id !== -1,
                );
                setSelectedMembers(filteredMembers);
              }
            }}
            renderInput={(params) => (
              <TextField {...params} label="Team Members" margin="normal" />
            )}
            renderTags={(
              value: (TeamMember | { id: number; name: string })[],
              getTagProps,
            ) =>
              value
                .filter((option) => option.id !== -1)
                .map((option, index) => (
                  <Chip
                    label={option.name}
                    {...getTagProps({ index })}
                    key={option.id}
                  />
                ))
            }
          />
        )}
        {recipientType === 'positions' && (
          <Autocomplete
            multiple
            size="small"
            options={[{ id: -1, s_title: 'Select All' }, ...positions]}
            getOptionLabel={(option) => option.s_title}
            value={selectedPositions}
            onChange={(_, newValue) => {
              const isSelectAll = newValue.some((p) => p.id === -1);
              if (isSelectAll) {
                const allPositions = positions.filter((p) => p.id !== -1);
                setSelectedPositions(allPositions);
              } else {
                setSelectedPositions(
                  newValue.filter((p): p is Position => p.id !== -1),
                );
              }
            }}
            renderInput={(params) => (
              <TextField {...params} label="Positions" margin="normal" />
            )}
            renderTags={(
              value: (Position | { id: number; s_title: string })[],
              getTagProps,
            ) =>
              value
                .filter((option) => option.id !== -1)
                .map((option, index) => (
                  <Chip
                    label={option.s_title}
                    {...getTagProps({ index })}
                    key={option.id}
                  />
                ))
            }
          />
        )}
        <TextField
          label="Message"
          fullWidth
          multiline
          minRows={4}
          margin="normal"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />

        <RadioGroup
          row
          value={sendMethod}
          onChange={(e) => setSendMethod(e.target.value as any)}
        >
          <FormControlLabel
            value="email"
            control={<Radio />}
            label="Email only"
          />
          <FormControlLabel
            value="text"
            control={<Radio />}
            label="Text only"
          />
          <FormControlLabel
            value="both"
            control={<Radio />}
            label="Email and Text"
          />
        </RadioGroup>

        {sendMethod !== 'email' && (
          <Box mt={2}>
            <Typography variant="body2" color="textSecondary">
              {textCount.characters} characters.
            </Typography>

            <Typography
              variant="body2"
              color={textCount.exceedsCredits ? 'error' : 'textSecondary'}
              mt={0.5}
            >
              {/* {textCount.totalCreditsUsed} / {MAX_CREDITS} texts used */}
              {totalCreditsUsed} / {MAX_CREDITS} texts used
            </Typography>

            {textCount.exceedsCredits && (
              <Box
                mt={1}
                p={2}
                bgcolor="#fff3e0"
                border="1px solid #ff9800"
                borderRadius={5}
              >
                <Typography variant="body2" color="text.primary">
                  Your plan includes {MAX_CREDITS} text to start. You have
                  exceed the limit, you’ll need to purchase additional texts at
                  $0.05 each, with a 500 text minimum at $25.
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  color="primary"
                  sx={{ textAlign: 'center', marginTop: '10px', width: '100%' }}
                  onClick={() => setShowPurchaseTextsModal(true)}
                >
                  Purchase more texts now
                </Button>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', marginBottom: '20px' }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled
          color="secondary"
          sx={{ width: '200px', fontSize: '1rem', padding: '5px' }}
        >
          Cancel
        </Button>
        <LoadingButton
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          disabled={
            !message ||
            (recipientType === 'members' && selectedMembers.length === 0) ||
            (recipientType === 'positions' && selectedPositions.length === 0) ||
            (sendMethod !== 'email' && textCount.exceedsCredits) ||
            isCreatingTeamMessage
          }
          loading={isCreatingTeamMessage}
          sx={{
            width: '200px',
            fontSize: '1rem',
            fontWeight: 'bold',
            padding: '5px',
          }}
        >
          Send Message
        </LoadingButton>
      </DialogActions>
      {showPurchaseTextsModal && (
        <Elements stripe={stripePromise}>
          <PurchaseTextsDialog
            open={showPurchaseTextsModal}
            handleClose={() => setShowPurchaseTextsModal(false)}
          />
        </Elements>
      )}
    </Dialog>
  );
};

export default MessageTeamDialog;
